import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Coins, GraduationCap, ArrowRight, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchCoinPackages } from "@/services/coinPurchaseService";
import { fetchPublicSubscriptionPlans } from "@/services/teacherSubscriptionService";
import { cn } from "@/lib/utils";
import { formatPackMinor, formatPlanMinor, packAmountMinorFor } from "@/lib/paymentAmounts";
import { PricesRetryAlert } from "@/components/coins/PricesRetryAlert";

/**
 * The home page's pricing block: BOTH sides of the model, condensed.
 *
 * Replaces PricingSnapshotSection, which showed only BMC Coins and so implied Coins were the whole
 * pricing story — the teacher tiers were invisible to anyone not already logged in. This is the
 * summary; /pricing carries the full comparison and is unchanged.
 *
 * Prices are FETCHED, never restated here. The section this replaces kept a hardcoded TOP_UPS
 * table with a comment asking future editors to keep it in step with the Liquibase seed by hand;
 * that is exactly the drift a public page cannot afford. The cheapest pack and cheapest plan are
 * derived from the same endpoints /pricing uses.
 *
 * Failure behaviour differs from /pricing deliberately. /pricing exists to state prices, so when it
 * cannot it says so plainly. Here the prices are a supporting detail on a marketing page, so a
 * failed fetch simply drops the "from ..." line and the rest of the section still reads correctly.
 * What it must never do — and does not — is fall back to a hardcoded number.
 */

const INQUIRY_COST_COINS = 30;
const SIGNUP_GRANT_COINS = 300;

export function PricingSection() {
  const navigate = useNavigate();

  const packagesQuery = useQuery({ queryKey: ["public", "coin-packages"], queryFn: fetchCoinPackages });
  const plansQuery = useQuery({ queryKey: ["public", "teacher-plans"], queryFn: fetchPublicSubscriptionPlans });

  // SCRUM-365: the plans currency exactly as returned -- no INR fallback (D3).
  const rawCurrency = plansQuery.data?.currency ?? null;
  const showPlansError = plansQuery.isError;

  /** Cheapest pack, so the headline figure is a real "from" price rather than a chosen one. */
  const cheapestPack = useMemo(() => {
    const packs = packagesQuery.data ?? [];
    if (packs.length === 0) return null;
    // Unknown currency has no amount (D2); fall back to the smallest pack by coins.
    const minor = (p: (typeof packs)[number]) => packAmountMinorFor(p, rawCurrency) ?? p.coins;
    return packs.reduce((lo, p) => (minor(p) < minor(lo) ? p : lo));
  }, [packagesQuery.data, rawCurrency]);
  const cheapestPackMinorOn = cheapestPack ? packAmountMinorFor(cheapestPack, rawCurrency) : null;

  const cheapestPlan = useMemo(() => {
    const plans = plansQuery.data?.plans ?? [];
    if (plans.length === 0) return null;
    return plans.reduce((lo, p) => (p.monthlyPriceMinor < lo.monthlyPriceMinor ? p : lo));
  }, [plansQuery.data]);

  const goToPricing = () => {
    navigate("/pricing");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className="py-24 bg-[#F7F7FC] dark:bg-muted/30" aria-labelledby="home-pricing-h">
      <div className="container mx-auto px-4 max-w-5xl">

        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(237,52%,50%)] mb-4 px-3 py-1 rounded-full bg-[hsl(237,52%,50%)]/10">
            <Sparkles className="w-3 h-3" aria-hidden="true" />
            Pricing
          </span>
          <h2 id="home-pricing-h" className="font-sans text-3xl sm:text-4xl font-black text-[hsl(237,52%,31%)] mb-3">
            One platform. Two ways to pay.
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Learners pay with BMC Coins, only when they reach out. Teachers can teach free, and
            choose a plan when they want more reach.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mb-10">

          {/* ── Learners (BMC Coins) ── */}
          <div className="rounded-2xl border border-border bg-card p-6 flex flex-col">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-[hsl(237,52%,50%)]/10 text-[hsl(237,52%,50%)]">
                <Coins className="w-5 h-5" aria-hidden="true" />
              </span>
              <h3 className="text-lg font-bold text-foreground">For learners</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Browsing is free. You only spend Coins to reach a teacher.
            </p>
            <ul className="space-y-2.5 mb-5">
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-foreground">
                  <span className="font-semibold">{SIGNUP_GRANT_COINS} free Coins</span> on signup — about{" "}
                  {SIGNUP_GRANT_COINS / INQUIRY_COST_COINS} inquiries
                </span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-foreground">{INQUIRY_COST_COINS} Coins to contact a teacher</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-foreground">No subscription — top up only when you run out</span>
              </li>
            </ul>
            {cheapestPack && !showPlansError && (
              <p className="text-sm text-muted-foreground mt-auto pt-4 border-t border-border">
                Coin packs from{" "}
                <span
                  className="font-bold text-foreground"
                  aria-label={cheapestPackMinorOn === null ? "Price unavailable" : undefined}
                >
                  {formatPackMinor(cheapestPackMinorOn, rawCurrency)}
                </span>{" "}
                for {cheapestPack.coins.toLocaleString()} Coins
              </p>
            )}
          </div>

          {/* ── Teachers (subscription tiers) ── */}
          <div className="rounded-2xl border border-border bg-card p-6 flex flex-col">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-500/10 text-amber-600">
                <GraduationCap className="w-5 h-5" aria-hidden="true" />
              </span>
              <h3 className="text-lg font-bold text-foreground">For teachers</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Teaching is free to start. A plan adds reach when you want it.
            </p>
            <ul className="space-y-2.5 mb-5">
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-foreground">
                  <span className="font-semibold">Publish free</span> — no card, no time limit
                </span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-foreground">Plans add more offerings and a search ranking boost</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-foreground">Monthly bonus Coins and cheaper replies</span>
              </li>
            </ul>
            {showPlansError && (
              <PricesRetryAlert onRetry={() => void plansQuery.refetch()} className="mt-auto" />
            )}
            {cheapestPlan && (
              <p className="text-sm text-muted-foreground mt-auto pt-4 border-t border-border">
                Paid plans from{" "}
                <span className="font-bold text-foreground">
                  {formatPlanMinor(cheapestPlan.monthlyPriceMinor, rawCurrency)}
                </span>
                /month
              </p>
            )}
          </div>
        </div>

        <div className="text-center">
          <Button
            onClick={goToPricing}
            className={cn("rounded-full font-bold cursor-pointer transition-colors duration-200")}
          >
            See full pricing <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            Compare every Coin pack and teacher plan side by side
          </p>
        </div>

      </div>
    </section>
  );
}
